import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateNotification, NotificationReadStatus } from 'src/api/updateNotification/updateNotification';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useNotifications } from 'src/hooks/useNotifications/useNotifications';
import { INotification } from 'src/api/getNotifications/getNotifications';

interface UpdateNotificationVariables {
  notificationId: string;
  status: NotificationReadStatus;
  action: string;
}

export const useUpdateNotification = () => {
  const queryClient = useQueryClient();
  const { captureException } = useUserTracking();
  const { data: notifications = [] } = useNotifications();

  const mutation = useMutation({
    mutationFn: ({ notificationId, status }: UpdateNotificationVariables) => updateNotification(notificationId, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QueryKeys.notifications] }),
    onError: (error, { action }) => captureException(error, { tags: { action } }),
  });

  const markRead = ({ notification_id }: INotification, action?: string) =>
    mutation.mutate({
      notificationId: notification_id,
      status: NotificationReadStatus.READ,
      action: action || 'mark_notification_read',
    });

  const markNotificationRead = (matcher: (notification: INotification) => boolean, action?: string) => {
    const notification = notifications.find(matcher);
    if (notification) {
      markRead(notification, action);
    }
  };

  const markNotificationsRead = (matcher: (notification: INotification) => boolean, action?: string) =>
    notifications.filter(matcher).forEach((notification) => markRead(notification, action));

  return { ...mutation, markNotificationRead, markNotificationsRead };
};
